import { useThemeContext } from "@/v2-ui/theme/theme.context"
import type { ThemeMode } from "@/v2-ui/theme/theme.types"

/**
 * Named, on-brand data-visualization colors — the single source for JS color
 * values used by charts, map symbols and markers (nivo / ArcGIS / Mapbox
 * can't read the `--token` CSS vars, so they need concrete values here).
 *
 * Consume via the single `DATA_VIZ` namespace:
 *   - `DATA_VIZ.brand.green2`, `DATA_VIZ.greenRamp[0]`, `DATA_VIZ.semantic.negative`
 *   - `DATA_VIZ.series[5]`, `DATA_VIZ.axis.dark`
 *
 * For per-theme values use `useThemeColors()` (React) or `getThemeColors(mode)`;
 * the sets are identical today (the on-brand greens read fine on every panel
 * surface), so both return `DATA_VIZ` — diverge the branches when a chart needs
 * per-mode colors.
 */
export const DATA_VIZ = {
  /** Brand single colors. */
  brand: {
    green: "#50d092",
    greenBright: "#6fff90",
    green2: "#1eda89",
    green3: "#0ac173",
    greenDeep: "#18754d",
    amber: "#fbbf24",
  },
  /**
   * Travel-time searches, by means of transport: the mode's base colour, the
   * first search on a source. Later searches of the same mode lighten from
   * it (the sketcher's shade ladder), so the rings read as one family fading
   * outwards to one light cap, so every base sits dark enough to hold on an
   * orthophoto and leave the ladder room (lightness 32 to 45 %). Driving is
   * the brand green two steps deep; transit is the one off-brand hue, so it
   * never reads as green.
   */
  travel: {
    driving: "#0d9458",
    walking: "#d97706",
    cycling: "#14b8a6",
    transit: "#be2a8a",
  },
  /**
   * Sequential brand-green ramp, light → dark (heatmaps, size buckets, stacks).
   * The Placepoint brand greens (NXT identity + MCP chart bars), not generic
   * Tailwind greens.
   */
  greenRamp: ["#ddffe5", "#6fff90", "#1eda89", "#0ac173", "#18754d"],
  /**
   * Semantic data colors (green positive / coral negative / slate neutral).
   * Coral (#ff6f6f, the boligtorken on-brand red) reads better than a hard red
   * for down / minus / negative on the dark-green panel surfaces.
   */
  semantic: {
    positive: "#16a34a",
    negative: "#ff6f6f",
    neutral: "#64748b",
  },
  /**
   * Axis ticks, gridlines and captions. Minted text at low opacity on dark
   * (Placepoint MCP `colors.muted`); deep green-black at low opacity on light.
   */
  axis: {
    dark: "rgba(221,255,229,0.65)",
    light: "rgba(7,33,27,0.55)",
  },
  /**
   * Map symbol colors (ArcGIS/Mapbox symbols can't read the `--token` CSS vars).
   * Neutral black hairline for cadastre / building-footprint parcel borders,
   * rendered at low opacity over the basemap. Brand chrome is green/white/black,
   * so black is on-brand here (see comment on `series`).
   */
  map: {
    parcelBorder: "#000000",
  },
  /**
   * Takmål 3D roof preview (the Placepoint Takmål viewer): dark brand-green
   * stage, pale walls, one soft color per roof tile (`tiles`), and one color
   * per edge type so the legend reads like the drawing (eave light, ridge
   * coral, hip/valley blue, step lilac). `hover` is the brand accent green.
   */
  takmal: {
    stage: "#0a241e",
    grid: "#1b4238",
    wall: "#c9d8d2",
    hover: "#6fff90",
    sky: "#ffffff",
    ground: "#2a4a42",
    fill: "#bfffe0",
    edgeEave: "#e8f3ef",
    edgeRidge: "#ff7b6e",
    edgeHip: "#7cc4ff",
    edgeStep: "#c9b0ff",
    edgeFaint: "#6d8c83",
    /**
     * One soft color per roof tile, cycled by tile number. Muted steps of the
     * brand hues (green 150 to 156, sage 165, mint 134, amber 38 to 43), with
     * greens and warm tones alternating so neighbouring tiles separate.
     */
    tiles: [
      "#77c5a3", // brand green, soft (hsl 154 40% 62%)
      "#eacd86", // amber, soft (hsl 43 70% 72%)
      "#7caba0", // sage (hsl 165 22% 58%)
      "#b2dcbc", // mint (hsl 134 38% 78%)
      "#509b7a", // deep sage green (hsl 154 32% 46%)
      "#cfb381", // sand (hsl 38 45% 66%)
      "#aecbc0", // pale green-grey (hsl 156 22% 74%)
      "#67ad8a", // mid green (hsl 150 30% 54%)
    ],
  },
  /**
   * Categorical series — the sanctioned Placepoint chart palette (Placepoint
   * MCP `get_brand_assets(chart_palette)`, proven in the shipped branded
   * reports). Brand CHROME is green/white/black, but data SERIES deliberately
   * use distinct hues (teal/blue/purple/pink) for categorical separation.
   */
  series: [
    "#6fff90", // brand accent green
    "#fbbf24", // amber
    "#14b8a6", // teal
    "#60a5fa", // blue
    "#a3e635", // lime
    "#c084fc", // purple
    "#f97316", // orange
    "#f472b6", // pink
    "#64748b", // slate
  ],
} as const

/** @knipignore -- #954 token API */
export type DataVizColors = typeof DATA_VIZ

/**
 * Resolve the data-viz color set for a theme. Sets are identical today;
 * branch here when a category needs per-mode values.
 * @knipignore -- #954 token API
 */
export function getThemeColors(
  _mode: ThemeMode,
  _theme?: string,
): DataVizColors {
  return DATA_VIZ
}

/** React hook: data-viz colors for the active theme (re-renders on change). */
export function useThemeColors(): DataVizColors {
  const { theme } = useThemeContext()
  return getThemeColors(theme.mode, theme.theme)
}
